import Swiper from "./swiper-bundle.esm.browser.min.js";
import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

const badges = document.querySelector(".photoswipe > .card__badges");

const lightbox = new PhotoSwipeLightbox({
  // may select multiple "galleries"
  gallery: ".photoswipe",

  // Elements within gallery (slides)
  children: "a",

  // setup PhotoSwipe Core dynamic import
  pswpModule: () => import("./photoswipe.esm.min.js"),

  bgOpacity: 1,
});
lightbox.init();
lightbox.on("beforeOpen", () => {
  badges?.classList.add("hide");
  document.querySelectorAll("gallery-section").forEach((gallery) => {
    gallery.pauseAutoplayForInteraction?.();
  });
});

lightbox.on("destroy", () => {
  badges?.classList.remove("hide");
  document.querySelectorAll("gallery-section").forEach((gallery) => {
    gallery.scheduleAutoplayResume?.();
  });
});

if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      updateSwiperAutoHeight = () => {
        if (this.resolution != this.matchResolution()) {
          this.resolution = this.matchResolution();
          this.destroyCarouselGallery();
          this.initializeGallery();
          this.setStickyGallery();
        }
      };

      constructor() {
        super();
        this._isSyncing = false;
        this._autoplayPausedByHover = false;
        this._autoplayPausedByInteraction = false;
        this._boundThumbsNext = null;
        this._boundThumbsPrev = null;
        this._boundMainNavInteraction = null;
        this._boundAutoplayEnter = null;
        this._boundAutoplayLeave = null;
        this._interactionResumeTimer = null;
        this._thumbScrollScheduled = false;
        this._pendingThumbScrollIndex = null;
        this._pendingThumbScrollSmooth = true;
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";

        this.galleryLoader = this.querySelector("#gallery-loader");
        this.renderedSlides = [];
        this.readConfiguration();
        this.initializeGallery();
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        this.classList.add("loaded");

        this.setStickyGallery();
      }

      disconnectedCallback() {
        this.destroyCarouselGallery();
      }

      setStickyGallery() {
        if (this.isProductPage) {
          const pageHeader = document.querySelector(".page-header");
          const headerBottom = pageHeader
            ? pageHeader.getBoundingClientRect().bottom
            : document.querySelector(".wt-header")?.offsetHeight || 0;
          const swiperRect = this.getBoundingClientRect();

          const fitsInViewport =
            swiperRect.top >= 0 &&
            swiperRect.left >= 0 &&
            swiperRect.height <= window.innerHeight;

          const positionTopValue = `${headerBottom + 16}px`;

          this.style.setProperty("--position-top", positionTopValue);
          this.classList.add("wt-product__gallery--sticky", fitsInViewport);
        }
      }

      readConfiguration() {
        // general configuration
        this.configuration = [];

        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]").querySelector(
            "[data-swiper-container]",
          ),
          thumbsContainer: this.querySelector("[data-thumbs]").querySelector(
            "[data-swiper-container]",
          ),
        };

        this.elements.gallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this.elements.thumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
          enableAutoplay: false,
          autoplayDelay: 5000,
          enableLoop: false,
          pauseOnHover: true,
          pauseOnInteraction: false,
        };

        const custom_configuration = JSON.parse(
          this.elements.section.querySelector("[data-configuration]").innerHTML,
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        const autoplayDelay = Number(this.configuration.autoplayDelay);
        this.configuration.autoplayDelay =
          Number.isFinite(autoplayDelay) && autoplayDelay >= 2000
            ? autoplayDelay
            : 5000;
        this.configuration.enableAutoplay = Boolean(
          this.configuration.enableAutoplay,
        );
        this.configuration.enableLoop = Boolean(this.configuration.enableLoop);
        this.configuration.pauseOnHover =
          this.configuration.pauseOnHover !== false;
        this.configuration.pauseOnInteraction = Boolean(
          this.configuration.pauseOnInteraction,
        );

        let autoHeightEnabled = window.innerWidth <= 768;

        const UNMUTE_CLASS_TOGGLE = "wt-hero-video__sound-toggle--unmuted";

        function handleSlideVideos(swiper) {
          const allVideos = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll("video")),
          );
          const allToggleBtns = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll(".wt-hero-video__sound-toggle")),
          );

          // pause, mute & disable controls for all videos
          allVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
            video.controls = false;
            clearTimeout(video._hideCtrlsTimer);
          });

          // reset all toggle buttons to "off" state
          allToggleBtns.forEach((btn) => {
            btn.setAttribute("data-sound", "off");
            btn.classList.remove(UNMUTE_CLASS_TOGGLE);
          });

          // play & keep muted the active slide's video
          const activeSlide = swiper.slides[swiper.activeIndex];
          const activeVideo = activeSlide?.querySelector("video");
          if (activeVideo) {
            activeVideo.muted = true;
            activeVideo.play().catch((err) => {
              console.warn("Video playback failed:", err);
            });
          }
        }

        function initVideoInteractions(swiper) {
          handleSlideVideos(swiper);

          // add delegation listener for clicks
          swiper.el.addEventListener("click", (e) => {
            // Video element tapped -> show native controls temporarily
            const video = e.target.closest("video");
            if (video) {
              video.controls = true;
              video.blur();
              clearTimeout(video._hideCtrlsTimer);
              video._hideCtrlsTimer = setTimeout(() => {
                video.controls = false;
              }, 3000);
              return;
            }
          });
        }

        // gallery swiper configuration
        const default_gallery_configuration = {
          autoHeight: autoHeightEnabled,
          threshold: 10,
          grabCursor: true,
          slidesPerView: 1,
          slidesPerGroup: 1,
          preventClicks: false,
          preventClicksPropagation: false,
          touchStartPreventDefault: false,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: {
            el: ".wt-slider__scrollbar",
            draggable: true,
          },
          pagination: {
            el: ".swiper-pagination",
            type: "fraction",
          },
          on: {
            afterInit: initVideoInteractions,
            slideChangeTransitionEnd: handleSlideVideos,
          },
        };

        const custom_gallery_configuration = JSON.parse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const updateThumbsSwiperClasses = (swiperInstance) => {
          const container = swiperInstance.el;
          const cls = {
            beginning: "swiper-at-beginning",
            end: "swiper-at-end",
            locked: "swiper-locked",
            ready: "swiper-thumbs-ready",
          };

          container.classList.remove(
            cls.beginning,
            cls.end,
            cls.locked,
            cls.ready,
          );

          if (swiperInstance.isLocked) {
            container.classList.add(cls.locked);
            return;
          }

          container.classList.add(cls.ready);

          if (swiperInstance.isBeginning)
            container.classList.add(cls.beginning);
          if (swiperInstance.isEnd) container.classList.add(cls.end);
        };

        // gallery thumbs swiper configuration
        // Navigation arrows are bound manually so they advance the main gallery
        // (and thus the active thumb) instead of only scrolling the thumb strip.
        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 2,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          slideToClickedSlide: false,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          on: {
            afterInit(swiper) {
              updateThumbsSwiperClasses(swiper);
            },
            lock: updateThumbsSwiperClasses,
            unlock: updateThumbsSwiperClasses,
            slideChange: updateThumbsSwiperClasses,
            observerUpdate: updateThumbsSwiperClasses,
            resize: updateThumbsSwiperClasses,
          },
        };

        const custom_thumbs_configuration = JSON.parse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
        this.applyThumbsVisibleCount();
        this.resolution = this.matchResolution();
      }

      getThumbsVisibleCount() {
        const fromDataset = parseInt(
          this.elements.thumbs?.dataset.thumbsVisible,
          10,
        );
        if (!Number.isNaN(fromDataset) && fromDataset > 0) return fromDataset;

        const fromCssVar = parseInt(
          getComputedStyle(this).getPropertyValue("--pdp-thumbs-visible"),
          10,
        );
        if (!Number.isNaN(fromCssVar) && fromCssVar > 0) return fromCssVar;

        return 2;
      }

      applyThumbsVisibleCount() {
        const thumbCount = this.getThumbsVisibleCount();
        const isCarousel =
          this.configuration.desktopLayout === "carousel-vertical" ||
          this.configuration.desktopLayout === "carousel-horizontal";

        if (!isCarousel || !this.thumbs_configuration) return;

        this.thumbs_configuration.slidesPerView = thumbCount;

        if (this.thumbs_configuration.breakpoints?.[768]) {
          this.thumbs_configuration.breakpoints[768] = {
            ...this.thumbs_configuration.breakpoints[768],
            slidesPerView: thumbCount,
            freeMode: false,
          };
        }

        if (this.thumbs_configuration.breakpoints?.[901]) {
          this.thumbs_configuration.breakpoints[901] = {
            ...this.thumbs_configuration.breakpoints[901],
            slidesPerView: thumbCount,
            freeMode: false,
          };
        }
      }

      applyThumbsVisibleCountForViewport() {
        const thumbCount = this.getThumbsVisibleCount();
        const isCarousel =
          this.configuration.desktopLayout === "carousel-vertical" ||
          this.configuration.desktopLayout === "carousel-horizontal";

        if (!isCarousel || !this.thumbs_configuration) return;

        if (window.innerWidth >= 768) {
          this.thumbs_configuration.slidesPerView = thumbCount;
          this.thumbs_configuration.freeMode = false;
          this.thumbs_configuration.slidesPerGroup = 1;
        }
      }

      initializeGallery() {
        window.addEventListener("resize", () => {
          this.updateSwiperAutoHeight(this.gallerySwiper);
        });

        switch (this.configuration.desktopLayout) {
          case "carousel-vertical":
          case "carousel-horizontal":
            this.initializeCarouselGallery();
            break;
          case "masonry":
            window.addEventListener(
              "resize",
              function () {
                this.handleMassonry();
              }.bind(this),
            );
            this.handleMassonry();
            break;
          case "collage":
            window.addEventListener(
              "resize",
              function () {
                this.handleCollage();
              }.bind(this),
            );
            this.handleCollage();
            break;
        }
      }

      initializeCarouselGallery() {
        if (this.thumbsSwiper == null) {
          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
          this.thumbsSwiper = this.swiperThumbsInitilize();
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize(this.thumbsSwiper);

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide) {
          disableTouchSlide.classList.add("swiper-no-swiping");
        }

        this.bindThumbsNavigation();
        this.bindMainNavigationInteraction();
        this.bindAutoplayInteractions();
        this.syncGalleries(this.gallerySwiper?.activeIndex ?? 0, "init");
      }

      swiperGalleryInitilize(thumbsSwiper) {
        if (this.gallerySwiper == null) {
          const thumbs_configuration = {
            thumbs: {
              swiper: thumbsSwiper,
              multipleActiveThumbs: false,
            },
          };
          const desktopRatio = this.getAttribute("desktop-ratio");

          const useAlwaysAutoHeight =
            this.configuration.desktopLayout === "carousel-vertical" &&
            desktopRatio === "original";

          let autoHeightEnabled = useAlwaysAutoHeight
            ? true
            : window.innerWidth <= 900;

          if (this.configuration.desktopLayout === "carousel-horizontal") {
            autoHeightEnabled = window.innerWidth <= 767;
          }
          if (thumbsSwiper)
            this.gallery_configuration = {
              ...this.gallery_configuration,
              ...thumbs_configuration,
              autoHeight: autoHeightEnabled,
            };

          const galleryNavNext = this.elements.gallery.querySelector(
            ".wt-slider__nav-next",
          );
          const galleryNavPrev = this.elements.gallery.querySelector(
            ".wt-slider__nav-prev",
          );
          if (galleryNavNext && galleryNavPrev) {
            this.gallery_configuration.navigation = {
              nextEl: galleryNavNext,
              prevEl: galleryNavPrev,
            };
          }

          const slideCount =
            this.elements.gallerySlides?.length ||
            this.elements.gallery.querySelectorAll("[data-swiper-slide]")
              .length;
          if (this.configuration.enableLoop && slideCount > 1) {
            this.gallery_configuration.loop = true;
          }

          if (this.configuration.enableAutoplay && slideCount > 1) {
            this.gallery_configuration.autoplay = {
              delay: this.configuration.autoplayDelay,
              disableOnInteraction: this.configuration.pauseOnInteraction,
              pauseOnMouseEnter: false,
              stopOnLastSlide: false,
            };
          } else {
            delete this.gallery_configuration.autoplay;
          }

          const existingGalleryOn = this.gallery_configuration.on || {};
          this.gallery_configuration.on = {
            ...existingGalleryOn,
            afterInit: (swiper) => {
              existingGalleryOn.afterInit?.(swiper);
              this.syncGalleries(swiper.activeIndex, "gallery");
            },
            slideChange: (swiper) => {
              existingGalleryOn.slideChange?.(swiper);
              this.syncGalleries(swiper.activeIndex, "gallery");
            },
            slideChangeTransitionEnd: (swiper) => {
              existingGalleryOn.slideChangeTransitionEnd?.(swiper);
            },
            touchStart: (swiper) => {
              existingGalleryOn.touchStart?.(swiper);
              this.pauseAutoplayForInteraction();
            },
            touchEnd: (swiper) => {
              existingGalleryOn.touchEnd?.(swiper);
              if (!this.configuration.pauseOnInteraction) {
                this.scheduleAutoplayResume();
              }
            },
            sliderFirstMove: (swiper) => {
              existingGalleryOn.sliderFirstMove?.(swiper);
              this.pauseAutoplayForInteraction();
            },
          };

          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.gallery_configuration,
          );
        }
      }

      syncGalleries(activeIndex, source = "gallery") {
        if (
          this._isSyncing ||
          activeIndex == null ||
          activeIndex < 0 ||
          !this.gallerySwiper ||
          this.gallerySwiper.destroyed
        ) {
          return;
        }

        this._isSyncing = true;

        try {
          if (
            source !== "gallery" &&
            source !== "init" &&
            this.gallerySwiper.activeIndex !== activeIndex
          ) {
            this.gallerySwiper.slideTo(activeIndex);
          }

          if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
            const thumbSlides = this.thumbsSwiper.slides || [];
            thumbSlides.forEach((slide, index) => {
              slide.classList.toggle(
                "swiper-slide-thumb-active",
                index === activeIndex,
              );
            });

            if (source !== "thumbs-scroll") {
              this.ensureActiveThumbVisible(activeIndex);
            }
          }

          this.syncThumbAccessibility(activeIndex);
          this.updateThumbsNavState();
        } finally {
          this._isSyncing = false;
        }
      }

      /**
       * Scroll the thumbnail strip only as far as needed so the active thumb
       * is fully inside the viewport. Does not pin the thumb to the start
       * (which creates empty left gaps with slidesPerView > 1).
       */
      ensureActiveThumbVisible(activeIndex, { smooth = true } = {}) {
        if (activeIndex == null || activeIndex < 0) return;

        this._pendingThumbScrollIndex = activeIndex;
        this._pendingThumbScrollSmooth = smooth;

        if (this._thumbScrollScheduled) return;
        this._thumbScrollScheduled = true;

        requestAnimationFrame(() => {
          this._thumbScrollScheduled = false;
          const index = this._pendingThumbScrollIndex;
          const useSmooth = this._pendingThumbScrollSmooth !== false;
          this._pendingThumbScrollIndex = null;
          this._scrollActiveThumbIntoView(index, useSmooth);
        });
      }

      _scrollActiveThumbIntoView(activeIndex, smooth = true) {
        const swiper = this.thumbsSwiper;
        if (
          !swiper ||
          swiper.destroyed ||
          activeIndex == null ||
          activeIndex < 0
        ) {
          return;
        }

        // Ensure Swiper geometry is current before measuring.
        if (typeof swiper.updateSize === "function") swiper.updateSize();
        if (typeof swiper.updateSlides === "function") swiper.updateSlides();

        const slideCount = swiper.slides?.length || 0;
        if (!slideCount || activeIndex >= slideCount) return;

        const slidesGrid = swiper.slidesGrid;
        const slidesSizesGrid = swiper.slidesSizesGrid;
        let slideStart = slidesGrid?.[activeIndex];
        let slideSize = slidesSizesGrid?.[activeIndex];

        // Fallback to DOM measurements when Swiper grids are unavailable.
        if (slideStart == null || slideSize == null || slideSize <= 0) {
          const slideEl = swiper.slides[activeIndex];
          if (!slideEl) return;
          const isVertical = swiper.isVertical;
          slideStart = isVertical ? slideEl.offsetTop : slideEl.offsetLeft;
          slideSize = isVertical
            ? slideEl.offsetHeight
            : slideEl.offsetWidth;
        }

        const containerSize = swiper.size;
        if (!containerSize) return;

        const minTranslate = swiper.minTranslate();
        const maxTranslate = swiper.maxTranslate();
        const currentTranslate = swiper.getTranslate();

        const viewStart = -currentTranslate;
        const viewEnd = viewStart + containerSize;
        const slideEnd = slideStart + slideSize;
        const epsilon = 1;

        const fullyVisible =
          slideStart >= viewStart - epsilon && slideEnd <= viewEnd + epsilon;

        if (fullyVisible) {
          this.updateThumbsOverflowClasses(swiper);
          return;
        }

        let targetTranslate = currentTranslate;

        if (activeIndex === 0) {
          targetTranslate = minTranslate;
        } else if (activeIndex === slideCount - 1) {
          targetTranslate = maxTranslate;
        } else if (slideStart < viewStart) {
          // Scroll just enough to reveal the left/top edge.
          targetTranslate = -slideStart;
        } else if (slideEnd > viewEnd) {
          // Scroll just enough to reveal the right/bottom edge.
          targetTranslate = -(slideEnd - containerSize);
        }

        // Clamp to valid scroll range (0 … scrollWidth - clientWidth).
        targetTranslate = Math.max(
          maxTranslate,
          Math.min(minTranslate, targetTranslate),
        );

        if (Math.abs(targetTranslate - currentTranslate) < epsilon) {
          this.updateThumbsOverflowClasses(swiper);
          return;
        }

        const speed = smooth ? Number(swiper.params.speed) || 300 : 0;

        if (typeof swiper.translateTo === "function") {
          swiper.translateTo(targetTranslate, speed, false, true);
        } else {
          swiper.setTransition(speed);
          swiper.setTranslate(targetTranslate);
          swiper.updateProgress(targetTranslate);
          swiper.updateActiveIndex();
          swiper.updateSlidesClasses?.();
        }

        this.updateThumbsOverflowClasses(swiper);
      }

      updateThumbsOverflowClasses(swiperInstance) {
        const swiper = swiperInstance || this.thumbsSwiper;
        if (!swiper || swiper.destroyed || !swiper.el) return;

        const container = swiper.el;
        const cls = {
          beginning: "swiper-at-beginning",
          end: "swiper-at-end",
          locked: "swiper-locked",
          ready: "swiper-thumbs-ready",
        };

        container.classList.remove(
          cls.beginning,
          cls.end,
          cls.locked,
          cls.ready,
        );

        if (swiper.isLocked) {
          container.classList.add(cls.locked);
          return;
        }

        container.classList.add(cls.ready);

        const translate = swiper.getTranslate();
        const minTranslate = swiper.minTranslate();
        const maxTranslate = swiper.maxTranslate();
        const epsilon = 1;

        if (Math.abs(translate - minTranslate) <= epsilon || swiper.isBeginning) {
          container.classList.add(cls.beginning);
        }
        if (Math.abs(translate - maxTranslate) <= epsilon || swiper.isEnd) {
          container.classList.add(cls.end);
        }
      }

      syncThumbAccessibility(activeIndex) {
        const thumbSlides = this.elements.thumbs?.querySelectorAll(
          "[data-swiper-slide]",
        );
        if (!thumbSlides?.length) return;

        thumbSlides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.setAttribute("role", "tab");
          slide.setAttribute("aria-selected", isActive ? "true" : "false");
          slide.setAttribute("aria-current", isActive ? "true" : "false");
          slide.setAttribute("tabindex", isActive ? "0" : "-1");
          slide.setAttribute(
            "aria-label",
            `Product image thumbnail ${index + 1} of ${thumbSlides.length}`,
          );
          slide.classList.toggle("swiper-slide-thumb-active", isActive);
        });
      }

      updateThumbsNavState() {
        const nextEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-next--thumbs",
        );
        const prevEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-prev--thumbs",
        );
        if (!nextEl || !prevEl || !this.gallerySwiper) return;

        const atStart = this.gallerySwiper.isBeginning;
        const atEnd = this.gallerySwiper.isEnd;

        prevEl.classList.toggle("swiper-button-disabled", atStart);
        prevEl.toggleAttribute("disabled", atStart);
        prevEl.setAttribute("aria-disabled", String(atStart));

        nextEl.classList.toggle("swiper-button-disabled", atEnd);
        nextEl.toggleAttribute("disabled", atEnd);
        nextEl.setAttribute("aria-disabled", String(atEnd));
      }

      bindThumbsNavigation() {
        this.unbindThumbsNavigation();

        const nextEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-next--thumbs",
        );
        const prevEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-prev--thumbs",
        );
        if (!nextEl || !prevEl) return;

        this._boundThumbsNext = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateGallery("next");
        };
        this._boundThumbsPrev = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateGallery("prev");
        };

        nextEl.addEventListener("click", this._boundThumbsNext);
        prevEl.addEventListener("click", this._boundThumbsPrev);
        this.updateThumbsNavState();
      }

      unbindThumbsNavigation() {
        const nextEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-next--thumbs",
        );
        const prevEl = this.elements.thumbs?.querySelector(
          ".wt-slider__nav-prev--thumbs",
        );

        if (nextEl && this._boundThumbsNext) {
          nextEl.removeEventListener("click", this._boundThumbsNext);
        }
        if (prevEl && this._boundThumbsPrev) {
          prevEl.removeEventListener("click", this._boundThumbsPrev);
        }

        this._boundThumbsNext = null;
        this._boundThumbsPrev = null;
      }

      bindMainNavigationInteraction() {
        this.unbindMainNavigationInteraction();

        const nextEl = this.elements.gallery?.querySelector(
          ".wt-slider__nav-next",
        );
        const prevEl = this.elements.gallery?.querySelector(
          ".wt-slider__nav-prev",
        );
        if (!nextEl && !prevEl) return;

        this._boundMainNavInteraction = () => {
          this.pauseAutoplayForInteraction();
          this.scheduleAutoplayResume();
        };

        nextEl?.addEventListener("click", this._boundMainNavInteraction);
        prevEl?.addEventListener("click", this._boundMainNavInteraction);
      }

      unbindMainNavigationInteraction() {
        const nextEl = this.elements.gallery?.querySelector(
          ".wt-slider__nav-next",
        );
        const prevEl = this.elements.gallery?.querySelector(
          ".wt-slider__nav-prev",
        );

        if (this._boundMainNavInteraction) {
          nextEl?.removeEventListener("click", this._boundMainNavInteraction);
          prevEl?.removeEventListener("click", this._boundMainNavInteraction);
        }

        this._boundMainNavInteraction = null;
      }

      navigateGallery(direction) {
        if (!this.gallerySwiper || this.gallerySwiper.destroyed) return;

        this.pauseAutoplayForInteraction();

        if (direction === "next") {
          this.gallerySwiper.slideNext();
        } else {
          this.gallerySwiper.slidePrev();
        }

        if (!this.configuration.pauseOnInteraction) {
          this.scheduleAutoplayResume();
        }
      }

      bindAutoplayInteractions() {
        this.unbindAutoplayInteractions();
        if (!this.configuration.enableAutoplay) return;
        if (!this.configuration.pauseOnHover) return;

        this._boundAutoplayEnter = () => {
          this._autoplayPausedByHover = true;
          this.pauseAutoplay();
        };
        this._boundAutoplayLeave = () => {
          this._autoplayPausedByHover = false;
          this.resumeAutoplay();
        };

        this.addEventListener("mouseenter", this._boundAutoplayEnter);
        this.addEventListener("mouseleave", this._boundAutoplayLeave);
      }

      unbindAutoplayInteractions() {
        if (this._boundAutoplayEnter) {
          this.removeEventListener("mouseenter", this._boundAutoplayEnter);
        }
        if (this._boundAutoplayLeave) {
          this.removeEventListener("mouseleave", this._boundAutoplayLeave);
        }
        this._boundAutoplayEnter = null;
        this._boundAutoplayLeave = null;

        if (this._interactionResumeTimer) {
          clearTimeout(this._interactionResumeTimer);
          this._interactionResumeTimer = null;
        }
      }

      pauseAutoplay() {
        if (this.gallerySwiper?.autoplay?.running) {
          this.gallerySwiper.autoplay.pause();
        }
      }

      pauseAutoplayForInteraction() {
        this._autoplayPausedByInteraction = true;
        this.pauseAutoplay();
        if (this._interactionResumeTimer) {
          clearTimeout(this._interactionResumeTimer);
          this._interactionResumeTimer = null;
        }
      }

      scheduleAutoplayResume() {
        if (!this.configuration.enableAutoplay) return;
        if (this.configuration.pauseOnInteraction) return;
        if (this._interactionResumeTimer) {
          clearTimeout(this._interactionResumeTimer);
        }

        this._interactionResumeTimer = setTimeout(() => {
          this._interactionResumeTimer = null;
          this._autoplayPausedByInteraction = false;
          this.resumeAutoplay();
        }, 400);
      }

      resumeAutoplay() {
        if (!this.configuration.enableAutoplay) return;
        if (this._autoplayPausedByHover || this._autoplayPausedByInteraction)
          return;
        if (!this.gallerySwiper?.autoplay) return;

        const autoplay = this.gallerySwiper.autoplay;
        if (autoplay.paused) {
          // Swiper 7 uses run(); resume() exists in later versions.
          if (typeof autoplay.resume === "function") {
            autoplay.resume();
          } else if (typeof autoplay.run === "function") {
            autoplay.run();
          } else {
            autoplay.start();
          }
        } else if (!autoplay.running) {
          autoplay.start();
        }
      }

      teardownGalleryControls() {
        this.unbindThumbsNavigation();
        this.unbindMainNavigationInteraction();
        this.unbindAutoplayInteractions();
        this._thumbScrollScheduled = false;
        this._pendingThumbScrollIndex = null;
        if (this.gallerySwiper?.autoplay?.running) {
          this.gallerySwiper.autoplay.stop();
        }
      }

      swiperThumbsInitilize() {
        this.applyThumbsVisibleCount();
        this.applyThumbsVisibleCountForViewport();

        let autoHeightEnabled = window.innerWidth <= 900;
        if (this.configuration.desktopLayout === "carousel-horizontal") {
          autoHeightEnabled = false;
        }
        this.thumbs_configuration = {
          ...this.thumbs_configuration,
          autoHeight: autoHeightEnabled,
          // Thumb arrows are handled in bindThumbsNavigation().
          navigation: false,
        };

        const existingThumbsOn = this.thumbs_configuration.on || {};
        this.thumbs_configuration.on = {
          ...existingThumbsOn,
          afterInit: (swiper) => {
            existingThumbsOn.afterInit?.(swiper);
            this.syncThumbAccessibility(
              this.gallerySwiper?.activeIndex ?? swiper.activeIndex,
            );
          },
          click: (swiper, event) => {
            existingThumbsOn.click?.(swiper, event);
            if (this._isSyncing) return;

            const clickedSlide = event?.target?.closest?.(
              ".swiper-slide, [data-swiper-slide]",
            );
            if (!clickedSlide) return;

            const clickedIndex = Array.from(swiper.slides).indexOf(
              clickedSlide,
            );
            if (clickedIndex < 0) return;

            this.pauseAutoplayForInteraction();
            this.syncGalleries(clickedIndex, "thumbs");
            this.scheduleAutoplayResume();
          },
        };

        const swiper = new Swiper(
          this.elements.thumbs,
          this.thumbs_configuration,
        );
        return swiper;
      }

      destroyCarouselGallery() {
        this.teardownGalleryControls();

        if (this.thumbsSwiper != null) {
          this.thumbsSwiper.destroy();
          this.thumbsSwiper = null;
          this.undecorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
        }
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      handleMassonry() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeMasonryGallery();
        } else {
          this.destroyMasonryGallery();
          this.initializeCarouselGallery();
        }
      }

      handleCollage() {
        if (this.matchResolution() == "desktop") {
          this.destroyCarouselGallery();
          this.initializeCollageGallery();
        } else {
          this.destroyCollageGallery();
          this.initializeCarouselGallery();
        }
      }

      decorateSwiper(el, element_class) {
        el.classList.add("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]")?.classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.galleryUpdateEvent();
      }

      undecorateSwiper(el, element_class) {
        el.classList.remove("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
      }

      decorateCollage(el, element_class) {
        el.classList.add(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.add(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add(`wt-${element_class}__slide`);
        });
        this.galleryUpdateEvent();
      }

      undecorateCollage(el, element_class) {
        el.classList.remove(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.remove(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove(`wt-${element_class}__slide`);
        });
      }

      initializeCollageGallery() {
        this.decorateCollage(this.elements.gallery, "collage");
      }

      destroyCollageGallery() {
        this.undecorateCollage(this.elements.gallery, "collage");
      }

      initializeMasonryGallery() {
        this.decorateCollage(this.elements.gallery, "masonry");
      }

      destroyMasonryGallery() {
        this.undecorateCollage(this.elements.gallery, "masonry");
      }

      sortSlides(slides, featured_media_id) {
        // Find the index of the slide with featured_media_id
        const featuredIndex = slides.findIndex((slide) => {
          const mediaId = slide.querySelector("img")
            ? Number(slide.querySelector("img").getAttribute("data-media-id"))
            : null;
          return mediaId === Number(featured_media_id);
        });

        // If a slide with featured_media_id is found, move it to the beginning of the array
        if (featuredIndex > -1) {
          const featuredSlide = slides[featuredIndex];
          slides.splice(featuredIndex, 1);
          slides.unshift(featuredSlide);
        }

        return slides;
      }

      beforeGalleryChange() {
        this.classList.add("loading");
        this.galleryLoader?.classList.remove("hidden");
        this.style.minHeight = `${this.offsetHeight}px`;
      }

      afterGalleryChange() {
        this.style.minHeight = "unset";
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
      }

      filterSlidesByOptions(
        slides,
        options,
        featured_media_id,
        matchAll = true,
      ) {
        const lowercaseOptions = options.map((option) =>
          option.toLowerCase().replace(/\s/g, ""),
        );

        return slides.filter((slide) => {
          let media = slide.querySelector("img");
          if (media == null) media = slide?.querySelector("video");
          const alt = media ? media.getAttribute("alt") : "";
          const mediaId = media ? media.getAttribute("data-media-id") : "";

          if (mediaId === featured_media_id) return true;

          const altHashtags = (alt?.match(/#[^\s#]+/g) || []).map((hashtag) =>
            hashtag.slice(1).toLowerCase(),
          );

          if (altHashtags.length === 0) return true;
          if (altHashtags.some((tag) => tag.split("|").includes("all")))
            return true;

          if (matchAll) {
            return altHashtags.every((tag) =>
              tag
                .split("|")
                .some((variant) => lowercaseOptions.includes(variant.trim())),
            );
          } else {
            return altHashtags.some((tag) =>
              tag
                .split("|")
                .some((variant) => lowercaseOptions.includes(variant.trim())),
            );
          }
        });
      }

      galleryUpdateEvent(opt) {
        const galleryEvent = new CustomEvent("gallery:updated", {
          bubbles: true,
          cancelable: true,
          detail: {
            desc: "gallery updated",
            selector: ".wt-product__gallery",
            ...opt,
          },
        });
        document.dispatchEvent(galleryEvent);
      }

      filterSlides(options, featured_media_id, matchAll = true, callback) {
        if (!this.elements) return;
        const originalGallerySlides = Array.from(
          this.elements.gallerySlides,
          (el) => el.cloneNode(true),
        );
        const originalThumbsSlides = Array.from(
          this.elements.thumbsSlides,
          (el) => el.cloneNode(true),
        );

        const getMediaId = (item) => item?.dataset?.mediaId;

        let filteredGallerySlides = this.filterSlidesByOptions(
          originalGallerySlides,
          options,
          featured_media_id,
          matchAll,
        );
        let filteredThumbsSlides = this.filterSlidesByOptions(
          originalThumbsSlides,
          options,
          featured_media_id,
          matchAll,
        );

        if (filteredGallerySlides.length === 0) {
          filteredGallerySlides = [...originalGallerySlides];
          filteredThumbsSlides = [...originalThumbsSlides];
        }

        if (featured_media_id) {
          filteredGallerySlides = this.sortSlides(
            filteredGallerySlides,
            featured_media_id,
          );
          filteredThumbsSlides = this.sortSlides(
            filteredThumbsSlides,
            featured_media_id,
          );
        }

        const renderedSlidesChanged =
          this.renderedSlides.map(getMediaId).toString() !==
          filteredGallerySlides.map(getMediaId).toString();

        if (this.gallerySwiper && this.thumbsSwiper && renderedSlidesChanged) {
          this.beforeGalleryChange();
          const thumbSlidesWrapper = this.querySelector(
            "[data-thumbs] [data-swiper-container]",
          );
          const gallerySlidesWrapper = this.querySelector(
            "[data-gallery] [data-swiper-container]",
          );

          // Assuming this.gallerySwiper is the swiper instance for the main gallery
          this.gallerySwiper.removeAllSlides();
          gallerySlidesWrapper.innerHTML = "";
          const swiperContainer = this.querySelector(".swiper-wrapper");
          filteredGallerySlides.forEach((slide, idx) => {
            if (idx === 0) {
              const slideImg = slide.querySelector("img");
              if (slideImg)
                slideImg.onload = function () {
                  swiperContainer.style.height = "auto";
                };
            }
            this.gallerySwiper.appendSlide(slide);
          });
          this.decorateSwiper(this.elements.gallery);

          // Assuming this.thumbsSwiper is the swiper instance for the thumbnails
          this.thumbsSwiper.removeAllSlides();
          thumbSlidesWrapper.innerHTML = "";

          filteredThumbsSlides.forEach((slide) =>
            this.thumbsSwiper.appendSlide(slide),
          );

          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );

          this.thumbsSwiper.update();
          this.gallerySwiper.update();
          this.bindThumbsNavigation();

          if (
            this.configuration.enableAutoplay &&
            filteredGallerySlides.length > 1
          ) {
            if (!this.gallerySwiper.params.autoplay) {
              this.gallerySwiper.params.autoplay = {
                delay: this.configuration.autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
                stopOnLastSlide: false,
              };
            }
            this.gallerySwiper.autoplay?.start?.();
          } else {
            this.gallerySwiper.autoplay?.stop?.();
          }
        } else if (
          this.configuration.desktopLayout == "collage" ||
          this.configuration.desktopLayout == "masonry"
        ) {
          if (renderedSlidesChanged) {
            this.beforeGalleryChange();
            // Remove all existing slides
            this.elements.gallery
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());
            this.elements.thumbs
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());

            filteredGallerySlides.forEach((slide) =>
              this.elements.gallery
                .querySelector("[data-swiper-container]")
                .append(slide),
            );
            filteredThumbsSlides.forEach((slide) =>
              this.elements.thumbs
                .querySelector("[data-swiper-container]")
                .append(slide),
            );

            this.decorateCollage(
              this.elements.gallery,
              this.configuration.desktopLayout,
            );

            // add wt-product__gallery --even --odd classes to MediaGallery container depends on number of slides
            this.elements.gallery.classList.remove("wt-product__gallery--even");
            this.elements.gallery.classList.remove("wt-product__gallery--odd");
            if (filteredGallerySlides.length % 2 == 0) {
              this.elements.gallery.classList.add("wt-product__gallery--even");
            } else {
              this.elements.gallery.classList.add("wt-product__gallery--odd");
            }
          }
        }

        if (featured_media_id) {
          this.setActiveMedia(featured_media_id, true);
        } else {
          this.setActiveMedia(
            filteredGallerySlides[0]
              ?.querySelector("img")
              ?.getAttribute("data-media-id"),
            true,
          );
        }
        this.gallerySwiper?.update();

        if (renderedSlidesChanged) {
          setTimeout(this.afterGalleryChange.bind(this), 300);
        }

        this.renderedSlides = [...filteredGallerySlides];
      }

      setActiveMedia(mediaId, prepend) {
        let media = this.elements.gallery.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.gallerySwiper != null) {
          const index = this.indexInParent(media);
          if (index < 0) return;
          this.syncGalleries(index, "variant");
        }
      }

      matchResolution() {
        if (window.innerWidth < this.configuration.sliderEnabledBreakpoint) {
          return "mobile";
        } else {
          return "desktop";
        }
      }

      indexInParent(node) {
        if (!node) return -1;
        let children = node.parentNode.childNodes;
        let num = 0;
        for (let i = 0; i < children.length; i++) {
          if (children[i] == node) return num;
          if (children[i].nodeType == 1) num++;
        }
        return -1;
      }
    },
  );
}
